import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../client'
import type {
  ChecklistDeRecebimento,
  ConformidadeDeGmud,
  RegistroDeOcorrencias,
  TipoDeRelatorio,
} from '../../types/relatorios'

/** As chaves de cache dos documentos do contrato. */
const chaves = {
  tudo: () => ['relatorios'] as const,
  tipos: () => ['relatorios', 'tipos'] as const,
  checklist: (competencia: string) => ['relatorios', 'recebimentos', competencia] as const,
  conformidade: (competencia: string) => ['relatorios', 'conformidade', competencia] as const,
  ocorrencias: () => ['relatorios', 'ocorrencias'] as const,
}

/**
 * Os relatórios que o contrato cobra e o dia limite de cada um.
 *
 * Sem competência: a pergunta é "que prazo está fixado hoje", e não "o que chegou naquele mês". É a
 * leitura da tela de administração, o único lugar de onde o dia limite pode ser fixado.
 *
 * @returns a consulta dos tipos
 */
export function useTiposDeRelatorio() {
  return useQuery({
    queryKey: chaves.tipos(),
    queryFn: async () => (await api.get<TipoDeRelatorio[]>('/relatorios/tipos')).data,
  })
}

/**
 * O checklist de recebimento de uma competência.
 *
 * @param competencia mês no formato `AAAA-MM`
 * @returns a consulta do checklist
 */
export function useChecklistDeRecebimento(competencia: string) {
  return useQuery({
    queryKey: chaves.checklist(competencia),
    queryFn: async () => (await api.get<ChecklistDeRecebimento>(`/relatorios/recebimentos/${competencia}`)).data,
    enabled: competencia.length > 0,
  })
}

/**
 * O relatório de conformidade das mudanças do mês.
 *
 * @param competencia mês no formato `AAAA-MM`
 * @returns a consulta da conformidade
 */
export function useConformidadeDeGmud(competencia: string) {
  return useQuery({
    queryKey: chaves.conformidade(competencia),
    queryFn: async () => (await api.get<ConformidadeDeGmud>(`/relatorios/conformidade/${competencia}`)).data,
    enabled: competencia.length > 0,
  })
}

/**
 * O registro contínuo de ocorrências administrativas.
 *
 * Sem competência de propósito: é o contrato inteiro numa lista, porque a reincidência é o que ele
 * pune com multa.
 *
 * @returns a consulta do registro
 */
export function useRegistroDeOcorrencias() {
  return useQuery({
    queryKey: chaves.ocorrencias(),
    queryFn: async () => (await api.get<RegistroDeOcorrencias>('/relatorios/ocorrencias')).data,
  })
}

/** O que a tela envia ao registrar que um relatório chegou. */
export interface RecebimentoParaRegistrar {
  competencia: string
  tipo: string
  entregueEm: string
  referencia: string
  observacao?: string
}

/**
 * Registra que um relatório da contratada chegou.
 *
 * @returns a mutação do registro
 */
export function useRegistrarRecebimento() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async ({ competencia, ...corpo }: RecebimentoParaRegistrar) =>
      (await api.post<ChecklistDeRecebimento>(`/relatorios/recebimentos/${competencia}`, corpo)).data,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.tudo() })
    },
  })
}

/**
 * Retira um registro de recebimento, sem apagá-lo.
 *
 * @returns a mutação da retirada
 */
export function useRevogarRecebimento() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async ({ competencia, tipo, motivo }: { competencia: string; tipo: string; motivo: string }) =>
      (await api.post<ChecklistDeRecebimento>(`/relatorios/recebimentos/${competencia}/${tipo}/revogar`, { motivo }))
        .data,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.tudo() })
    },
  })
}

/**
 * Fixa o dia limite de entrega de um tipo de relatório. Ato de administrador.
 *
 * @returns a mutação do prazo
 */
export function useDefinirPrazoDeRelatorio() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async ({ tipo, diaLimite }: { tipo: string; diaLimite: number | null }) =>
      (await api.post<TipoDeRelatorio[]>(`/relatorios/tipos/${tipo}/prazo`, { diaLimite })).data,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.tudo() })
    },
  })
}
